"use client";

import { useMemo, useState } from "react";

const leadsPerMonth = 3;

const currencyFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

type PricingCalculatorProps = {
  defaultValue?: number;
};

export default function PricingCalculator({
  defaultValue = 500,
}: PricingCalculatorProps) {
  const [avgCustomerValue, setAvgCustomerValue] = useState(defaultValue);

  const { monthlyRevenue, roi } = useMemo(() => {
    const revenue = avgCustomerValue * leadsPerMonth;
    return {
      monthlyRevenue: revenue,
      roi: revenue - 449,
    };
  }, [avgCustomerValue]);

  const roiTone = roi >= 0 ? "text-[#10B981]" : "text-[#E63946]";

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_1.2fr] items-start">
      <label className="block">
        <span className="text-sm font-medium text-[rgb(var(--text-muted-strong))]">
          Was verdienen Sie durchschnittlich pro Neukunde?
        </span>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={avgCustomerValue}
            onChange={(event) => {
              const nextValue = Number(event.target.value);
              setAvgCustomerValue(Number.isNaN(nextValue) ? 0 : nextValue);
            }}
            className="w-full rounded-xl border border-[#1B3A60]/25 bg-[rgb(var(--bg-base))] px-4 py-3 text-[rgb(var(--text-primary))] focus:outline-none focus:ring-2 focus:ring-[#1B3A60]/40"
          />
          <span className="text-lg text-[rgb(var(--text-muted-strong))]">€</span>
        </div>
      </label>

      <div className="rounded-2xl border border-[#10B981]/20 bg-[#10B981]/10 p-5">
        <p className="text-sm text-[rgb(var(--text-muted-strong))]">
          Beispielrechnung auf Basis von Erfahrungswerten (12-30 Anfragen/Monat in vergleichbaren Projekten):
        </p>
        <div className="mt-4 space-y-2 text-sm text-[rgb(var(--text-muted-strong))]">
          <div className="flex items-center justify-between">
            <span>Neukunden/Monat (konservativ):</span>
            <span className="font-semibold text-[rgb(var(--text-primary))]">
              {leadsPerMonth} Kunden
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Zusatz-Umsatz/Monat:</span>
            <span className="font-semibold text-[#10B981]">
              {formatCurrency(monthlyRevenue)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Investment:</span>
            <span className="font-semibold text-[rgb(var(--text-primary))]">249-449 €</span>
          </div>
          <div className="flex items-center justify-between border-t border-[#10B981]/20 pt-3 mt-3">
            <span className="font-semibold text-[rgb(var(--text-primary))]">ROI:</span>
            <span className={`text-lg font-bold ${roiTone}`}>
              {formatCurrency(roi)} / Monat
            </span>
          </div>
          <p className="mt-3 text-xs text-[rgb(var(--text-muted))]">
            Beispielrechnung, keine Garantie.
          </p>
        </div>
      </div>
    </div>
  );
}
